import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PendingClients from './pendingClients';
import IntakeLinkDrawer from '../../components/modals/intakeLinkDrawer';
import { getPendingIntakeCount } from '../../services/clientIntakes';
import { Dropdown } from 'react-bootstrap';
import ReactToPrint from 'react-to-print';
import { initColumns, debounce, rsdFormat, toLocalDateString } from '../../utils/helper';
import Pages from '../../components/pages';
import SetClientModal from './clientsSet';
import { deleteClient, getPaginatedClients } from '../../services/clients';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSheetExport } from '../../utils/queryHooks/sheetExport';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import OverlayLoader from '../../components/overlayLoader';
import ConfirmationModal from '../../components/modals/confirmation';
import SetTempEditOrderModal from '../../components/modals/orderEditTemp';
import { SkeletonRows } from '../../components/skeleton';

const PlusIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);

const SearchIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

const UsersIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.9"/>
    </svg>
);

/** Return initials from client.name ("First Last" → "FL") */
const getInitials = (client) => {
    const parts = (client.name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return '?';
};

/** Extract the city segment from the combined address string "street, city, state, zip, country" */
const extractCity = (address) => {
    if (!address) return '—';
    const parts = address.split(',');
    return parts.length >= 2 ? parts[1].trim() : parts[0].trim();
};

/** Cycle through 5 gradient classes based on index */
const AV_CLASSES = ['b1', 'b2', 'b3', 'b4', 'b5'];
const avClass = (idx) => AV_CLASSES[idx % AV_CLASSES.length];

const ClientsPage = () => {

    const { t } = useTranslation();
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    // 'all' | 'pending' — pending = customer sign-up requests awaiting review
    const tab = searchParams.get('tab') === 'pending' ? 'pending' : 'all'
    const setTab = (next) => setSearchParams(next === 'pending' ? { tab: 'pending' } : {})
    const [linkDrawer, setLinkDrawer] = useState(false)
    const { data: pendingCountData } = useQuery(['client-intakes', 'pending-count'], getPendingIntakeCount, { staleTime: 60 * 1000, refetchOnWindowFocus: true })
    const pendingCount = pendingCountData?.code === 200 ? Number(pendingCountData.message) || 0 : 0

    const [setClientModal, setSetClientModal] = useState(false)
    const [confirmationModal, setConfirmationModal] = useState(false)
    const [editJobModal, setEditJobModal] = useState(false)

    const [clients, setClients] = useState([])
    const [clientToEdit, setClientToEdit] = useState()
    const [clientToDelete, setClientToDelete] = useState()
    const [selectedJobId, setSelectedJobId] = useState()

    const [pageSize, setPageSize] = useState(10)
    const [searchText, setSearchText] = useState('')
    const [pagination, setPagination] = useState()
    const [pageNumber, setPageNumber] = useState(1)

    const printRef = useRef()
    const exportHeaders = [t('common.name'), t('common.phone'), t('common.address'), t('common.email'), t('common.notes'), t('clients.lastJob'), t('clients.totalJobsLabel')]
    const { exportSheet: exportExcel, isExporting } = useSheetExport('Clients',
        clients.map(({ id, ...rest }) => rest),
        exportHeaders)
    const { exportSheet: exportCSV, isExportingCSV } = useSheetExport('Clients',
        clients.map(({ id, ...rest }) => rest),
        exportHeaders, 'csv')

    const { data, isFetching } = useQuery(
        ['clients', 'paginated', pageNumber, pageSize, searchText],
        () => getPaginatedClients(pageNumber, pageSize, searchText)
    )

    const queryClient = useQueryClient()
    const { mutate, isLoading: isDeleting } = useMutation(deleteClient, {
        onSuccess: onMutateSuccess(() => {
            if (clients.length === 1 && pageNumber > 1) setPageNumber(pageNumber - 1)
            queryClient.invalidateQueries(['clients', 'paginated'])
        }, t('clients.clientDeleted'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setPagination(result)
            setClients(result.data)
        })
    }, [data])

    const handleDeleteClient = () => mutate(clientToDelete.id)

    const handleShowSetClientModal = (client) => {
        setClientToEdit(client)
        setSetClientModal(true)
    }

    const handleShowConfirmation = (client) => {
        setConfirmationModal(true)
        setClientToDelete(client)
    }

    const handleSearchText = debounce((e) => setSearchText(e.target.value))

    const handleSetPageSize = (size) => {
        setPageNumber(1)
        setPageSize(size)
    }

    const handleEditJob = (jobId) => {
        setSelectedJobId(jobId)
        setEditJobModal(true)
    }

    const totalClients = pagination?.totalRecords ?? clients.length

    return (
        <div className="sb-page sb-tpage">
            <SetClientModal display={setClientModal} toggle={setSetClientModal} clientId={clientToEdit?.id} />
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('clients.deleteClient')}
                message={t('clients.deleteClientConfirm')} confirmAction={handleDeleteClient} />
            <SetTempEditOrderModal display={editJobModal} toggle={setEditJobModal} jobId={selectedJobId} />
            <IntakeLinkDrawer show={linkDrawer} onHide={() => setLinkDrawer(false)} />

            <div className="sbr">

                {/* ── DESKTOP header (hidden on mobile) ── */}
                <div className="dhead sbr-d">
                    <div>
                        <div className="ttl">{t('clients.title')}</div>
                        <div className="sub">{t('clients.subtitle')}</div>
                    </div>
                    <div className="act">
                        {/* Desktop inline search — shrinks so the buttons keep their labels */}
                        <div className="hsearch">
                            <SearchIcon />
                            <input type="search" placeholder={t('clients.searchClients')} onChange={handleSearchText} />
                        </div>

                        {/* Export dropdown */}
                        <Dropdown>
                            <Dropdown.Toggle as="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" id="clients-export-d" disabled={isFetching}>
                                {t('common.export')}
                                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item as="button">
                                    <ReactToPrint trigger={() => <span>{t('clients.print')}</span>} content={() => printRef.current} />
                                </Dropdown.Item>
                                <Dropdown.Item as="button" disabled={isExportingCSV} onClick={() => exportCSV()}>
                                    {isExportingCSV ? t('clients.exporting') : t('clients.csv')}
                                </Dropdown.Item>
                                <Dropdown.Item as="button" disabled={isExporting} onClick={() => exportExcel()}>
                                    {isExporting ? t('clients.exporting') : t('clients.excel')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setLinkDrawer(true)}>
                            {t('clients.signUpLink')}
                        </button>
                        <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetClientModal()}>
                            <PlusIcon /> {t('clients.addClient')}
                        </button>
                    </div>
                </div>

                {/* ── MOBILE page header (hidden on desktop) ── */}
                <div className="sbr-m" style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div>
                            <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-1px', color: 'var(--ink)', lineHeight: 1 }}>{t('clients.title')}</div>
                            <div style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600, marginTop: '4px' }}>{t('clients.subtitle')}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {/* Export sits with the other page actions, the same as on
                                desktop, rather than buried in the list toolbar. */}
                            <Dropdown>
                                <Dropdown.Toggle as="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" id="clients-export-m" disabled={isFetching}>
                                    {t('common.export')}
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item as="button">
                                        <ReactToPrint trigger={() => <span>{t('clients.print')}</span>} content={() => printRef.current} />
                                    </Dropdown.Item>
                                    <Dropdown.Item as="button" disabled={isExportingCSV} onClick={() => exportCSV()}>
                                        {isExportingCSV ? t('clients.exporting') : t('clients.csv')}
                                    </Dropdown.Item>
                                    <Dropdown.Item as="button" disabled={isExporting} onClick={() => exportExcel()}>
                                        {isExporting ? t('clients.exporting') : t('clients.excel')}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            <button className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setLinkDrawer(true)}>{t('clients.link')}</button>
                            <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetClientModal()}>
                                <PlusIcon /> {t('clients.addClient')}
                            </button>
                        </div>
                    </div>

                    {/* Mobile search bar */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        background: '#fff', border: '1px solid var(--line)', borderRadius: '14px',
                        padding: '11px 14px', boxShadow: 'var(--sh-1)', marginBottom: '12px'
                    }}>
                        <SearchIcon />
                        <input
                            style={{ flex: 1, border: 0, outline: 0, fontFamily: 'inherit', fontSize: '14.5px', fontWeight: 600, color: 'var(--text)', background: 'transparent' }}
                            type="search"
                            placeholder={t('clients.searchPlaceholder')}
                            onChange={handleSearchText}
                        />
                    </div>
                </div>

                {/* ── All clients / Pending requests ── */}
                <div className="seg" style={{ marginBottom: 14 }}>
                    <div className={`chip${tab === 'all' ? ' active' : ''}`} onClick={() => setTab('all')}>{t('clients.allClients')}</div>
                    <div className={`chip${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')}>
                        {t('clients.pendingRequests')}{pendingCount > 0 && <span className="c">{pendingCount}</span>}
                    </div>
                </div>

                {tab === 'pending' && <PendingClients />}

                {tab === 'all' && <>
                {/* ── KPI strip (desktop: 4-up grid; mobile: 2-up count strip) ── */}
                {/* Desktop KPIs */}
                <div className="kpis4 sbr-d">
                    <div className="kpi lift">
                        <div className="top">
                            <div className="ig">
                                <UsersIcon />
                            </div>
                        </div>
                        <div className="l">
                            <UsersIcon />
                            {t('clients.totalClients')}
                        </div>
                        <div className="n num">{totalClients.toLocaleString('sr-RS')}</div>
                        <div className="sub2">{t('clients.allTimeOnBooks')}</div>
                    </div>
                    <div className="kpi">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
                            </svg>
                            {t('clients.newThisMonth')}
                        </div>
                        <div className="n num">—</div>
                        <div className="sub2 d flat">{t('clients.filteredResults')}</div>
                    </div>
                    <div className="kpi">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                            </svg>
                            {t('clients.active90d')}
                        </div>
                        <div className="n num">—</div>
                        <div className="sub2 d flat">{t('clients.needsBackendField')}</div>
                    </div>
                    <div className="kpi">
                        <div className="l">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                            {t('clients.avgLifetimeValue')}
                        </div>
                        <div className="n sm num" style={{ fontSize: '18px' }}>— RSD</div>
                        <div className="sub2 d flat">{t('clients.derivableFromData')}</div>
                    </div>
                </div>

                {/* Mobile KPI count strip */}
                <div className="sbr-m" style={{ marginBottom: '14px' }}><div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: '11px' }}>
                    <div className="kpi lift" style={{ padding: '14px 15px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: 'rgba(255,255,255,.72)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <UsersIcon />
                            {t('clients.totalClients')}
                        </div>
                        <div className="n num" style={{ color: '#fff', fontSize: '34px', fontWeight: 800, letterSpacing: '-1.6px', margin: '7px 0 2px', lineHeight: 1 }}>
                            {totalClients.toLocaleString('sr-RS')}
                        </div>
                        <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'rgba(255,255,255,.6)', marginTop: '6px' }}>{t('clients.allOnBooks')}</div>
                    </div>
                    <div className="kpi" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            {t('clients.onThisPage')}
                        </div>
                        <div className="n num" style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-1.1px', color: 'var(--ink)', margin: '6px 0 2px', lineHeight: 1 }}>
                            {clients.length}
                        </div>
                        <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--good)', marginTop: '6px' }}>{t('clients.ofTotal', { total: totalClients.toLocaleString('sr-RS') })}</div>
                    </div>
                </div></div>

                {/* ── Toolbar: count label + page-size + export (desktop only shows these inline) ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 800, color: 'var(--ink)', letterSpacing: '-.2px' }}>
                        {t('clients.allClients')}
                        <small style={{ fontWeight: 600, color: 'var(--muted)', marginLeft: '6px' }}>
                            {totalClients > 0 ? totalClients.toLocaleString('sr-RS') : ''}
                        </small>
                    </div>

                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <select
                            style={{
                                background: '#fff', border: '1px solid var(--line)', borderRadius: '10px',
                                padding: '6px 10px', fontSize: '12.5px', fontWeight: 700, color: 'var(--text)',
                                boxShadow: 'var(--sh-1)', cursor: 'pointer', fontFamily: 'inherit'
                            }}
                            onChange={(e) => handleSetPageSize(e.target.value)}
                            defaultValue="10"
                        >
                            <option value="10">{t('clients.perPage', { count: 10 })}</option>
                            <option value="20">{t('clients.perPage', { count: 20 })}</option>
                            <option value="50">{t('clients.perPage', { count: 50 })}</option>
                            <option value="100">{t('clients.perPage', { count: 100 })}</option>
                            <option value="250">{t('clients.perPage', { count: 250 })}</option>
                        </select>

                    </div>
                </div>

                {/* ── DESKTOP: table card ── */}
                <div className="card-sbr sbr-d" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px 0', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: '9px', marginBottom: 0, paddingBottom: '14px' }}>
                        <UsersIcon />
                        <span style={{ fontSize: '13.5px', fontWeight: 800, color: 'var(--ink)', letterSpacing: '-.2px' }}>{t('clients.allClients')}</span>
                        {pagination && (
                            <span style={{ marginLeft: 'auto', fontSize: '12.5px', fontWeight: 700, color: 'var(--muted)' }}>
                                {t('clients.showingOf', { count: clients.length, total: totalClients.toLocaleString('sr-RS') })}
                            </span>
                        )}
                    </div>

                    <div style={{ position: 'relative', minHeight: '120px' }} ref={printRef}>
                        {clients.length > 0 ? (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--line)' }}>
                                        {[
                                            { label: t('clients.client'),       align: 'left'  },
                                            { label: t('clients.district'),     align: 'left'  },
                                            { label: t('clients.phone'),        align: 'left'  },
                                            { label: t('clients.jobs'),         align: 'right' },
                                            { label: t('clients.lifetimeSpent'), align: 'right' },
                                            { label: t('common.status'),        align: 'right' },
                                        ].map(({ label, align }) => (
                                            <th key={label} style={{
                                                padding: '10px 16px', textAlign: align,
                                                fontSize: '11px', fontWeight: 800, color: 'var(--muted)',
                                                textTransform: 'uppercase', letterSpacing: '.5px',
                                                whiteSpace: 'nowrap'
                                            }}>{label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.map((client, idx) => {
                                        const initials = getInitials(client)
                                        const av = avClass(idx)
                                        const city = extractCity(client.address)
                                        const isActive = (client.totalJobs ?? 0) > 0
                                        const AV_BG = {
                                            b1: 'linear-gradient(160deg,#3B74F2,#1D4FD8)',
                                            b2: 'linear-gradient(160deg,#34D399,#0EA5E9)',
                                            b3: 'linear-gradient(160deg,#A78BFA,#7C3AED)',
                                            b4: 'linear-gradient(160deg,#FB7185,#E11D48)',
                                            b5: 'linear-gradient(160deg,#F59E0B,#D97706)',
                                        }
                                        return (
                                            <tr key={client.id} className="sb-tr" style={{ borderBottom: '1px solid var(--line-soft)' }}>
                                                {/* Client */}
                                                <td style={{ padding: '12px 16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        {client.avatarUrl
                                                            ? <img src={client.avatarUrl} alt={client.name || ''} style={{ width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0, objectFit: 'cover', border: '1px solid var(--line)' }} />
                                                            : <div style={{
                                                                width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: '13px', fontWeight: 800, color: '#fff', letterSpacing: '.2px',
                                                                background: AV_BG[av]
                                                            }}>
                                                                {initials}
                                                            </div>
                                                        }
                                                        <div>
                                                            <div
                                                                title={t('clients.viewClient')}
                                                                onClick={() => navigate(`/clients/${client.id}`)}
                                                                style={{ fontSize: '14px', fontWeight: 800, color: 'var(--ink)', letterSpacing: '-.2px', lineHeight: 1.2, cursor: 'pointer' }}
                                                            >
                                                                {client.name || '—'}
                                                            </div>
                                                            {client.email && (
                                                                <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--faint)', marginTop: '2px' }}>
                                                                    {client.email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                {/* District */}
                                                <td style={{ padding: '12px 16px' }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: 'var(--text)', fontSize: '13.5px' }}>
                                                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--muted)', flexShrink: 0 }}>
                                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                                        </svg>
                                                        {city}
                                                    </span>
                                                </td>
                                                {/* Phone */}
                                                <td style={{ padding: '12px 16px', fontSize: '13.5px', fontWeight: 600, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
                                                    {client.phone || '—'}
                                                </td>
                                                {/* Jobs badge */}
                                                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        minWidth: '30px', height: '24px', padding: '0 9px', borderRadius: '8px',
                                                        background: 'var(--surface)', border: '1px solid var(--line)',
                                                        fontSize: '12.5px', fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        {client.totalJobs ?? 0}
                                                    </span>
                                                </td>
                                                {/* Lifetime spent */}
                                                <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13.5px', fontWeight: 800, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                    {client.totalAmount ? rsdFormat(+client.totalAmount) : '—'}
                                                </td>
                                                {/* Status + actions */}
                                                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                                                        <span className={`pill ${isActive ? 'paid' : 'inactive'}`}>
                                                            {isActive ? t('clients.active') : t('clients.inactive')}
                                                        </span>
                                                        <button title={t('common.edit')} className="sb-tbl-act act-primary" onClick={() => handleShowSetClientModal(client)}>
                                                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                            </svg>
                                                        </button>
                                                        <button title={t('common.delete')} className="sb-tbl-act" onClick={() => handleShowConfirmation(client)}>
                                                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                                                                <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            isFetching ? (
                                <div style={{ padding: '16px 20px' }}>
                                    <SkeletonRows rows={6} widths={['20%', '14%', '18%', '8%', '16%', '10%']} />
                                </div>
                            ) : (
                                <div className="sbr-empty">
                                    <UsersIcon />
                                    {t('clients.noClients')}
                                </div>
                            )
                        )}

                        {((isFetching && clients.length > 0) || isDeleting) && <OverlayLoader />}
                    </div>

                    {pagination?.totalPages > 0 && (
                        <div style={{ padding: '12px 20px 18px', borderTop: '1px solid var(--line-soft)' }}>
                            <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                        </div>
                    )}
                </div>

                {/* ── MOBILE: row list ── */}
                <div className="sbr-m">
                    <div className="sbr" style={{ position: 'relative', minHeight: '80px' }}>
                        {clients.length > 0 ? (
                            clients.map((client, idx) => {
                                const initials = getInitials(client)
                                const av = avClass(idx)
                                const fullName = client.name || '—'
                                const district = extractCity(client.address)
                                const isActive = (client.totalJobs ?? 0) > 0
                                const grad = av === 'b1' ? 'linear-gradient(160deg,#3B74F2,#1D4FD8)'
                                    : av === 'b2' ? 'linear-gradient(160deg,#34D399,#0EA5E9)'
                                    : av === 'b3' ? 'linear-gradient(160deg,#A78BFA,#7C3AED)'
                                    : av === 'b4' ? 'linear-gradient(160deg,#FB7185,#E11D48)'
                                    : 'linear-gradient(160deg,#F59E0B,#D97706)'

                                return (
                                    <div key={client.id} className="mcl-card" onClick={() => navigate(`/clients/${client.id}`)}>
                                        <div className="mcl-top">
                                            {client.avatarUrl
                                                ? <img className="a" src={client.avatarUrl} alt={fullName} style={{ objectFit: 'cover', border: '1px solid var(--line)' }} />
                                                : <div className="a" style={{ background: grad, border: 0, color: '#fff' }}>{initials}</div>}
                                            <div className="mcl-id">
                                                <div className="mcl-nm">
                                                    <span className="t">{fullName}</span>
                                                    <span className={`mcl-pill ${isActive ? 'on' : 'off'}`}>{isActive ? t('clients.active') : t('clients.inactive')}</span>
                                                </div>
                                                <div className="mcl-loc">
                                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                                    {[district, client.phone].filter(s => s && s !== '—').join(' · ')}
                                                </div>
                                            </div>
                                            <svg className="mcl-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                        </div>
                                        <div className="mcl-stats">
                                            <div className="mcl-stat"><div className="l">{t('clients.jobs')}</div><div className="v">{client.totalJobs ?? 0}</div></div>
                                            <div className="mcl-stat"><div className="l">{t('clients.lifetime')}</div><div className="v">{client.totalAmount ? rsdFormat(+client.totalAmount) : '—'}</div></div>
                                            <div className="mcl-stat"><div className="l">{t('clients.outstanding')}</div><div className={`v${client.unpaidAmount > 0 ? ' due' : ''}`}>{client.unpaidAmount ? rsdFormat(+client.unpaidAmount) : '0'}</div></div>
                                        </div>
                                        {client.lastJobDate &&
                                            <div className="mcl-foot">{t('clients.lastJob')} · {toLocalDateString(client.lastJobDate, 'dd.MM.yyyy')}</div>}
                                    </div>
                                )
                            })
                        ) : (
                            isFetching ? (
                                <SkeletonRows rows={6} widths={['100%']} />
                            ) : (
                                <div className="sbr-empty">
                                    <UsersIcon />
                                    {t('clients.noClients')}
                                </div>
                            )
                        )}

                        {((isFetching && clients.length > 0) || isDeleting) && <OverlayLoader />}
                    </div>

                    {pagination?.totalPages > 0 && (
                        <div style={{ padding: '4px 0 16px' }}>
                            <Pages paginationData={pagination} changePage={setPageNumber} pageNumber={pageNumber} />
                        </div>
                    )}
                </div>
                </>}

            </div>{/* .sbr */}
        </div>
    );
};

export default ClientsPage;
